import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { HowToSchema, BreadcrumbSchema } from '../components/JsonLdSchema';

const steps = [
  {
    num: '01', title: 'Seamless QR Session',
    desc: "Customers scan a unique table-specific QR code to initiate their session. No app download required—browsing starts instantly in any mobile browser."
  },
  {
    num: '02', title: 'OTP-Quick Login',
    desc: 'Secure guest identity with zero-friction OTP login. This creates a customer profile instantly, allowing for loyalty tracking and personalized experiences.'
  },
  {
    num: '03', title: 'Direct DPOS Sync',
    desc: 'Real-time bi-directional synchronization. Orders push instantly to your DPOS terminal and print directly to the Kitchen Order Ticket (KOT) printer.'
  },
  {
    num: '04', title: 'Integrated Payments',
    desc: 'Built-in checkout via Razorpay and UPI. Secured transactions with automatic settlement tracking within your DPOS dashboard.'
  },
  {
    num: '05', title: 'Real-time Confirmation',
    desc: 'The customer receives a live order status page. Every update—from "Kitchen Accepted" to "Food Ready"—is reflected instantly on their screen.'
  },
];

const metrics = [
  { value: '< 200ms', label: 'Latency' },
  { value: 'Direct KOT', label: 'Instant Print' },
  { value: 'Razorpay', label: 'Native SDK' },
  { value: '99.9%', label: 'UPTIME SLA' },
];

const stats = [
  { value: '< 200ms', label: 'Latency', sub: 'System Speed' },
  { value: 'Direct KOT', label: 'Instant Print', sub: '' },
  { value: 'Razorpay', label: 'Native SDK', sub: '' },
  { value: '99.9%', label: 'UPTIME SLA', sub: 'Enterprise-grade infrastructure ensuring your kitchen never stops.' },
  { value: '< 2s', label: 'MENU LOAD TIME', sub: 'Optimized assets for fast ordering even on 2G/3G networks.' },
  { value: '100%', label: 'DATA OWNERSHIP', sub: 'Every customer profile and order history belongs to you.' },
];

export default function OnlineOrdering() {
  return (
    <main style={{ paddingTop: 64 }}>
      <SEOHead
        title="Online Ordering & QR Ordering System for Restaurants | DPOS"
        description="DPOS Online Ordering: QR-code table ordering, OTP guest login, and real-time sync to your POS terminal and kitchen order ticket (KOT) printer."
        canonical="/online-ordering"
      />
      <HowToSchema
        name="How DPOS QR Online Ordering Works"
        description="The end-to-end flow from a customer scanning a table QR code to the kitchen receiving and fulfilling the order through DPOS."
        steps={steps}
      />
      <BreadcrumbSchema items={[
        { name: 'Home', path: '/' },
        { name: 'Online Ordering' },
      ]} />
      {/* Header */}
      <section style={{ padding: '72px 24px 64px', background: '#fff' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{
            display: 'inline-block', background: '#111827', color: '#fff',
            fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
            padding: '4px 12px', borderRadius: 4, marginBottom: 24
          }}>Technical Workflow Focus</div>

          <h1 style={{ fontSize: 'clamp(32px, 4vw, 56px)', fontWeight: 800, color: '#111827', marginBottom: 16, lineHeight: 1.15 }}>
            The Anatomy of Seamless QR Ordering
          </h1>
          <p style={{ fontSize: 17, color: '#6b7280', maxWidth: 580, lineHeight: 1.7 }}>
            Explore the technical journey of a customer order, from the initial QR scan to the instant KOT print in your kitchen.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section style={{ padding: '0 24px 80px', background: '#fff' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 0 }}>
          {steps.map((step, i) => (
            <div key={step.num} style={{
              display: 'grid', gridTemplateColumns: '80px 1fr',
              gap: 24, padding: '32px 0',
              borderBottom: i < steps.length - 1 ? '1px solid #f3f4f6' : 'none',
              alignItems: 'flex-start'
            }}>
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: '#9ca3af',
                textTransform: 'uppercase', paddingTop: 4
              }}>STEP {step.num}</div>
              <div>
                <h3 style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 10 }}>{step.title}</h3>
                <p style={{ fontSize: 15, color: '#6b7280', lineHeight: 1.7, maxWidth: 600 }}>{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Metrics bar */}
      <section style={{ background: '#111827', padding: '48px 24px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 32 }}>
          {metrics.map(m => (
            <div key={m.label} style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 22, fontWeight: 800, color: '#CC0000', marginBottom: 4 }}>{m.value}</p>
              <p style={{ fontSize: 12, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{m.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats grid */}
      <section style={{ padding: '80px 24px', background: '#f9fafb' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
          {[
            { value: '99.9%', label: 'UPTIME SLA', desc: 'Enterprise-grade infrastructure ensuring your kitchen never stops.' },
            { value: '< 2s', label: 'MENU LOAD TIME', desc: 'Optimized assets for fast ordering even on 2G/3G networks.' },
            { value: '100%', label: 'DATA OWNERSHIP', desc: 'Every customer profile and order history belongs to you.' },
          ].map(s => (
            <div key={s.label} style={{
              background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16,
              padding: '32px 28px', borderTop: '4px solid #CC0000'
            }}>
              <p style={{ fontSize: 36, fontWeight: 800, color: '#111827', marginBottom: 4 }}>{s.value}</p>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: '#CC0000', textTransform: 'uppercase', marginBottom: 12 }}>{s.label}</p>
              <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6 }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '80px 24px', background: '#fff', textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 800, color: '#111827', marginBottom: 32 }}>
            Ready to streamline your restaurant workflow?
          </h2>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" style={{
              background: '#CC0000', color: '#fff', padding: '12px 28px',
              borderRadius: 8, fontWeight: 600, fontSize: 15
            }}>Setup Your Workflow</Link>
            <Link to="/contact" style={{
              background: '#fff', color: '#111827', padding: '12px 28px',
              borderRadius: 8, fontWeight: 600, fontSize: 15, border: '1px solid #e5e7eb'
            }}>Talk to an Expert</Link>
          </div>
        </div>
      </section>

      <section style={{ padding: '56px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', marginBottom: 20 }}>Guides on QR Ordering</h2>
          <ul style={{ display: 'flex', flexDirection: 'column', gap: 12, listStyle: 'none', padding: 0 }}>
            <li><Link to="/blog/how-qr-menu-ordering-works-restaurants" style={{ color: '#CC0000', fontWeight: 600 }}>How QR menu ordering works for restaurants</Link></li>
          </ul>
        </div>
      </section>
    </main>
  );
}
